import { Component, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputType, InputOption } from '../../models/assessment-definition.model';

// Angular Material Imports
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSliderModule } from '@angular/material/slider';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-dynamic-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatSliderModule,
    MatRadioModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './dynamic-input.component.html',
  styleUrls: ['./dynamic-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DynamicInputComponent),
      multi: true,
    },
  ],
})
export class DynamicInputComponent implements ControlValueAccessor {
  @Input() inputType: InputType = InputType.Number;
  @Input() options: InputOption[] = [];
  @Input() min?: number;
  @Input() max?: number;
  @Input() step: number = 1;
  @Input() disabled: boolean = false;
  @Input() sortReversed: boolean = false; // Neue Eingabe für die Sortierkonfiguration
  @Input() guideVisible: boolean = false; // Neuer Input für den Guide-Zustand

  value: any;
  inputTypes = InputType;

  // Ticks für Slider
  sliderTickMarks: number[] = [];

  // Timer-spezifische Variablen
  timerRunning: boolean = false;
  timerStartTime?: number;
  timerInterval?: any;

  onChange: any = () => {};
  onTouch: any = () => {};

  constructor() {}

  ngOnInit() {
    // Slider-Ticks erzeugen
    if (this.inputType === InputType.Slider && this.min !== undefined && this.max !== undefined) {
      const range = this.max - this.min;
      const tickStep = range > 10 ? Math.floor(range / 10) : 1;
      for (let i = this.min; i <= this.max; i += tickStep) {
        this.sliderTickMarks.push(i);
      }
      // Sicherstellen, dass max auch in den Ticks enthalten ist
      if (this.sliderTickMarks[this.sliderTickMarks.length - 1] !== this.max) {
        this.sliderTickMarks.push(this.max);
      }
    }
  }

  // ControlValueAccessor-Interface
  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Value Handling
  updateValue(event: any) {
    const newValue = event?.target?.value !== undefined ? event.target.value : event;
    this.value = newValue;
    this.onChange(newValue);
    this.onTouch();
  }

  // Timer-Funktionen
  startTimer() {
    if (!this.timerRunning) {
      this.timerRunning = true;
      this.timerStartTime = Date.now();
      this.timerInterval = setInterval(() => {
        if (this.timerStartTime) {
          const elapsedTime = (Date.now() - this.timerStartTime) / 1000;
          this.value = parseFloat(elapsedTime.toFixed(1));
          this.onChange(this.value);
        }
      }, 100);
    }
  }

  stopTimer() {
    if (this.timerRunning) {
      clearInterval(this.timerInterval);
      this.timerRunning = false;
      this.timerStartTime = undefined;
      this.onTouch();
    }
  }

  resetTimer() {
    this.stopTimer();
    this.value = 0;
    this.onChange(this.value);
    this.onTouch();
  }

  // Hilfsfunktion für radioGroup und dropdown
  compareOptionValues(o1: any, o2: any): boolean {
    return o1 === o2;
  }

  // Tracker für ngFor
  trackByOptionValue(index: number, option: InputOption): any {
    return option.value;
  }

  // Füge eine Methode für displaySliderLabel hinzu, die in der HTML-Vorlage verwendet wird
  displaySliderLabel(value: number): string {
    return `${value}`;
  }

  // Gibt sortierte Optionen zurück (entweder normal oder umgekehrt)
  get sortedOptions(): InputOption[] {
    if (!this.options || this.options.length === 0) return [];
    
    if (this.sortReversed) {
      // Erstelle eine neue Kopie des Arrays, um die ursprünglichen Daten nicht zu verändern
      return [...this.options].sort((a, b) => {
        // Bei numerischen Werten sortieren wir absteigend
        if (typeof a.value === 'number' && typeof b.value === 'number') {
          return b.value - a.value;
        }
        // Bei String-Werten sortieren wir nach dem Standardvergleich
        return String(a.value).localeCompare(String(b.value));
      });
    }
    
    return this.options;
  }
}
